import { auth } from "@/firebase";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { useEffect } from "react";
import Cookies from "js-cookie";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useBuy } from "@/context/buy";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { useLoading } from "@/context/loading";

export default function index() {
  const router = useRouter();
  const {
    openSingleModal,
    setLoggedUserDetails,
    getBuyModalData,
    modelMap,
    setModalMap,
  } = useBuy();
  const { startLoading } = useLoading();

  useEffect(() => {
    const signIn = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get("email");
        if (isSignInWithEmailLink(auth, window.location.href)) {
          const result = await signInWithEmailLink(auth, email, window.location.href);
          const token = await result.user.getIdToken();

          const formData = new FormData();
          formData.append("action", "magicLogin");
          formData.append("firebase_token", token);

          const response = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await response.json();

          localStorage.clear();
          Cookies.remove("user_data");
          sessionStorage.clear();

          if (data.status) {
            if (data.profile_status == 0) {
              const tempRegisterData = {
                email: email,
                web_token: data.token,
              };
              localStorage.setItem("temp_register_data", JSON.stringify(tempRegisterData));
              router.push(`/register?email=${email}`);
              return;
            }

            const storingData = {
              profile: data.data.profile,
              web_token_expires_at: data.web_token_expires_at,
              web_token: data.token,
              name: data?.data?.name,
              state: data?.data?.state,
              user_type: data?.data?.user_type,
            };

            const encryptedData = encryptInteger(JSON.stringify(storingData));
            Cookies.set("user_data", JSON.stringify(storingData), {
              expires: 10,
              path: "/",
              secure: true,
              sameSite: "Lax",
            });
            localStorage.setItem("user_data", encryptedData);

            setLoggedUserDetails({
              status: true,
              data: storingData,
              remove: false,
            });

            if (getBuyModalData()) openSingleModal("buy");
            if (modelMap) setModalMap(false);

            const last_url = sessionStorage.getItem("last_url");
            
            // Check if last_url is a payment success page
            // Always redirect to dashboard instead - user has already seen payment success
            if (last_url && last_url.includes("/payment-success")) {
              router.push("/dashboard");
              return;
            }
            
            // Check if last_url is a ticket confirmation page
            if (last_url && last_url.includes("/tickets/")) {
              // Validate ticket exists before redirecting
              try {
                // Extract ticket ID from URL (handle both full URLs and paths)
                const urlPath = last_url.includes("http") 
                  ? new URL(last_url).pathname 
                  : last_url.split("?")[0].split("#")[0];
                const ticketId = urlPath.split("/tickets/")[1];
                
                if (ticketId) {
                  const formData = new FormData();
                  formData.append("action", "FetchTicketDetails_v3");
                  formData.append("booking_id", ticketId);

                  const ticketResponse = await fetch(
                    `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
                    {
                      method: "POST",
                      headers: { Authorization: `Bearer ${storingData.web_token}` },
                      body: formData,
                    }
                  );

                  const ticketData = await ticketResponse.json();
                  
                  // If ticket doesn't exist or is invalid, redirect to dashboard instead
                  if (!ticketData.status) {
                    toast.error("Ticket not found. Redirecting to your dashboard.");
                    router.push("/dashboard");
                    return;
                  }
                  
                  // Ticket is valid, redirect to it
                  router.push(last_url);
                  return;
                } else {
                  // Invalid ticket URL format, redirect to dashboard
                  router.push("/dashboard");
                  return;
                }
              } catch (error) {
                console.error("Error validating ticket:", error);
                // If validation fails, redirect to dashboard
                router.push("/dashboard");
                return;
              }
            }
            
            router.push(last_url || "/dashboard");
          } else {
            toast.error(data?.message || "Login failed. Please try again.", {
              duration: 3000,
            });
            router.push("/");
          }
        } else {
          toast.error("Invalid or expired magic link. Please try again.", {
            duration: 3000,
          });
          router.push("/");
        }
      } catch (error) {
        console.error("Firebase login error:", error);
        const errorMsg =
          error?.code === "auth/invalid-action-code" || error?.message?.includes("expired")
            ? "Magic link expired. Please request a new one."
            : "Something went wrong. Please try again.";

        toast.error(errorMsg, {
          duration: 3000,
        });

        router.push("/");
      }
    };

    signIn();
  }, []);

  return <div>{startLoading("Logging in...")}</div>;
}
